/* global gtag */

// written from a copy of GAModule and following this guide:
// https://developers.google.com/analytics/devguides/migration/ua/analyticsjs-to-gtagjs
// and https://developers.google.com/analytics/devguides/collection/gtagjs/pages

import { MODULE_GTAG } from '../analyticsTypes'
import BasicModule from './BasicModule'
import { logDebug } from '../utils'

export default class GTagModule extends BasicModule {
  constructor () {
    super(MODULE_GTAG)
  }

  init (initConf = {}) {
    // Load the gtag snippet
    window.dataLayer = window.dataLayer || []
    window.gtag = window.gtag || function () {
      window.dataLayer.push(arguments)
    }

    const newScript = document.createElement('script')
    const firstScript = document.getElementsByTagName('script')[0]
    newScript.setAttribute('async', '')
    newScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(initConf.measurementId)
    firstScript.parentNode.insertBefore(newScript, firstScript)

    const mandatoryParams = ['measurementId']
    mandatoryParams.forEach(el => {
      if (!initConf[el]) throw new Error(`VueAnalytics : Please provide a "${el}" from the config.`)
    })

    this.config.debug = initConf.debug
    this.config.measurementId = initConf.measurementId

    gtag('js', new Date())
    gtag('config', initConf.measurementId, { send_page_view: false })
  }

  // Methods

  /**
   * Dispatch a view analytics event
   *
   * params object should contain
   * @param {string} viewName - The name of the view
   */
  trackView ({ viewName }) {
    if (this.config.debug) {
      logDebug(viewName)
    }

    gtag('event', 'page_view', {
      page_title: viewName,
      send_to: this.config.measurementId
    })
  }

  /**
   * Dispatch a tracking analytics event
   *
   * params object should contain
   * @param {string} category - Typically the object that was interacted with (e.g. 'Video')
   * @param {string} action - The type of interaction (e.g. 'play')
   * @param {string} label - Useful for categorizing events (e.g. 'Fall Campaign')
   * @param {integer} value - A numeric value associated with the event (e.g. 42)
   */
  trackEvent ({ category = 'engagement', action, label = null, value = null, callback = null }) {
    if (this.config.debug) {
      logDebug(...arguments)
    }

    gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    })
  }

  /**
   * Track an exception that occurred in the application.
   * https://developers.google.com/analytics/devguides/collection/analyticsjs/exceptions
   *
   * @param {string} description - Something describing the error (max. 150 Bytes)
   * @param {boolean} isFatal - Specifies whether the exception was fatal
   */
  trackException ({ description = '', isFatal = false }) {
    if (this.config.debug) {
      logDebug({ description, isFatal })
    }
    gtag('event', 'exception', {
      description: description,
      fatal: isFatal
    })
  }

  /**
   * Track an user timing to measure periods of time.
   * https://developers.google.com/analytics/devguides/collection/analyticsjs/user-timings
   *
   * @param {string} timingCategory - A string for categorizing all user timing variables into logical groups (e.g. 'JS Dependencies').
   * @param {string} timingVar -  A string to identify the variable being recorded (e.g. 'load').
   * @param {number} timingValue - The number of milliseconds in elapsed time to report to Google Analytics (e.g. 20).
   * @param {string|null} timingLabel -  A string that can be used to add flexibility in visualizing user timings in the reports (e.g. 'Google CDN').
   */
  trackTiming (timingCategory, timingVar, timingValue, timingLabel = null) {
    if (this.config.debug) {
      logDebug({ timingCategory, timingVar, timingValue, timingLabel })
    }
    gtag('event', 'timing_complete', {
      name: timingVar,
      value: timingValue,
      event_category: timingCategory,
      event_label: timingLabel
    })
  }

  setUsername (userId) {
    gtag('config', this.config.measurementId, {
      user_id: userId
    })
  }

  // Same as setUsername
  identify ({ userId }) {
    this.setUsername(userId)
  }

  setUserProperties ({ properties }) {
    // this.setDimensionsAndMetrics(properties)
  }
}
